import {Table, TableNode} from '@table-library/react-table-library'
import {usePagination} from '@table-library/react-table-library/pagination'
import React, {useEffect, useState} from 'react'
import {addDeps, isDef} from '@appscience/utils'
import {DataTableActionsSection, getDataTableActionsSectionProps} from './content/actions-section/actions-section'
import {DataTableContent} from './content/content'
import {DataTableContextInfo, DataTableContextProvider, DataTableRefsContext} from './context'
import {getPaginationSizes} from './external-kit'
import {useDataTableCallbacksRef} from './hooks/use-callbacks-ref'
import {useDataTableSort} from './hooks/use-data-table-sort'
import {useDataTableFilteredData} from './hooks/use-filtered-data'
import {DataTableProps} from './model'
import {useDataTableTheme} from './theme/use-data-table-theme'
import {getDataTableStateCheckers, useDataTablePrevValuesRef} from './hooks/use-prev-values-ref'
import DataTablePagination from './content/pagination-section'
import {useDataTableRowSelect} from './hooks/use-row-select'

export function DataTable<ColumnID extends string, ITEM extends TableNode>(props: DataTableProps<ColumnID, ITEM>) {
  //TODO:data-table Замемоизировать всё, что можно
  const {
    pagination,
    select: initSelectData,
    layout,
    columns: allColumns,
  } = props
  const selectControlled = isDef(initSelectData?.selectedRowIds)
  const [searchValue, setSearchValue] = useState('')
  const callbacksRef = useDataTableCallbacksRef()
  const prevValuesRef = useDataTablePrevValuesRef()
  const stateCheckerFns = getDataTableStateCheckers()
  const {columns, data} = useDataTableFilteredData({
    ...props,
    searchValue,
  })
  const theme = useDataTableTheme(props)
  const selectData = useDataTableRowSelect(data, initSelectData)
  const paginationData = usePagination(data, {
    state: pagination
      ? { size: pagination.initSize, page: pagination.initPageIndex || 0 }
      : { size: Infinity, page: 0 },
  })
  const sort = useDataTableSort({ columns, data })
  callbacksRef.current = {
    pagination: paginationData.fns,
    select: selectData && selectData.tableSelect.fns,
    sort: sort.fns,
  }
  const actionsSectionProps = getDataTableActionsSectionProps(props)

  const sortWasChanged = stateCheckerFns.checkSortChanged(prevValuesRef.current, sort)
  if (sortWasChanged) {
    callbacksRef.current.select && callbacksRef.current.select.onRemoveAll() //TODO: data-table разобраться почему не сбрасывается.
  }

  useEffect(() => {
    addDeps(data)
    if (!selectControlled) {
      //Если select не контролируется, всегда очищаем состояние при полном обновлении данных страницы.
      callbacksRef.current.select && callbacksRef.current.select.onRemoveAll()
    }
  }, [data, callbacksRef, selectControlled])

  function getContextValue(): DataTableContextInfo {
    return {
      select: selectData?.selectContext,
      // @ts-ignore
      allColumns: allColumns,
      setSearchValue,
      data: data.nodes,
      searchValue,
    }
  }

  return (
    <div>
      <DataTableContextProvider value={getContextValue()}>
        <DataTableRefsContext.Provider value={{ callbacksRef }}>
          {!!actionsSectionProps && <DataTableActionsSection {...actionsSectionProps} />}
          <Table pagination={paginationData} data={data} theme={theme} select={selectData && selectData.tableSelect} sort={sort} layout={{custom: true, ...layout}}>
            {tableList => {
              const rows = tableList as Array<ITEM>
              return <DataTableContent
                loading={props.loading}
                rows={rows}
                columns={columns}
                select={selectData && selectData.selectContext}
                sort={sort}
                filtersData={props.filtersData}
                getShortcutActions={props.getShortcutActions}
                interactiveRowsData={props.interactiveRowsData}
                onRowDoubleClick={props.onRowDoubleClick}
                summaryRow={props.summaryRow}
              />
            }}
          </Table>
        </DataTableRefsContext.Provider>
      </DataTableContextProvider>
      {!!pagination && <DataTablePagination
        pageIndex={paginationData.state.page}
        size={paginationData.state.size}
        totalRecordsCount={data.nodes.length}
        sizesList={pagination.sizesList || getPaginationSizes()}
        onPageIndexChange={index => {
          pagination.onPageIndexChange && pagination.onPageIndexChange(index)
          paginationData.fns.onSetPage(index)
        }}
        onSizeChange={size => {
          pagination.onSizeChange && pagination.onSizeChange(size)
          paginationData.fns.onSetSize(size)
        }}
      />}
    </div>
  )
}